// Used by the booking pages (/book-flight, /book-bus, /book-bus/status) to tell <LeadBot />
// what the user is going through. The page stays in charge of its data; the bot only reads.
import { useEffect } from 'react';
import {
    setLeadSignals,
    setLeadBotSuppressed,
    resetPageSignals,
    getLeadBotState,
    requestLeave,
} from './leadBotStore';

const IDLE_MS = 45000;                         // no typing / tapping on the form for 45s
const AWAY_MS = 30000;                         // 30s+ in another tab, then came back
const IDLE_STAGES = ['review', 'passenger'];   // form stages where "stuck" makes sense

/** 'dd-mm-yyyy' | 'yyyy-mm-dd' | ISO datetime → 'yyyy-mm-dd' ('' if unknown) */
export const toISODate = (raw) => {
    if (!raw) return '';
    const s = String(raw);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const m = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

/**
 * Leave a booking page through the bot's exit survey.
 * Accounts for the extra history entry the back guard adds, so one tap really leaves.
 */
export const leaveBookingPage = (navigate, trigger = 'bookLeave') =>
    requestLeave(() => {
        const guarded = !!(window.history.state && window.history.state.lbGuard);
        navigate(guarded ? -2 : -1);
    }, { trigger });

export const useBookingLeadSignals = ({
    booking,                 // { mode, stage, src_name, src_code, dest_name, dest_code, travel_date, amount, rcBookingId, prefillPhone, key }
    stage,
    bookingFailed = false,
    paymentFailed = false,
    bookError = false,
    paymentDismissed = false,
    suppress = {},
    dockOffset = 0,
    guardBack = false,       // intercept the browser back button once to ask why they're leaving
}) => {
    const key = booking?.key || '';

    // Page lifecycle
    useEffect(() => {
        setLeadSignals({ page: 'booking' });
        return () => resetPageSignals();
    }, []);

    // Booking context (published only when its content changes)
    const bookingJson = JSON.stringify(booking || null);
    useEffect(() => {
        setLeadSignals({ booking: booking || null, bookingKey: key });
    }, [bookingJson]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setLeadSignals({ bookingFailed, paymentFailed, bookError, paymentDismissed, dockOffset });
    }, [bookingFailed, paymentFailed, bookError, paymentDismissed, dockOffset]);

    const suppressKey = JSON.stringify(suppress);
    useEffect(() => {
        setLeadBotSuppressed(suppress);
    }, [suppressKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Stuck on the form: no input for IDLE_MS on a form stage
    useEffect(() => {
        setLeadSignals({ formIdle: false });
        if (!IDLE_STAGES.includes(stage)) return undefined;
        let timer = null;
        const arm = () => {
            clearTimeout(timer);
            timer = setTimeout(() => setLeadSignals({ formIdle: true }), IDLE_MS);
        };
        const events = ['keydown', 'pointerdown', 'input'];
        events.forEach(e => document.addEventListener(e, arm, true));
        arm();
        return () => {
            clearTimeout(timer);
            events.forEach(e => document.removeEventListener(e, arm, true));
        };
    }, [stage, key]);

    // Came back after a while in another tab
    useEffect(() => {
        setLeadSignals({ bookReturned: false });
        let hiddenAt = null;
        const onVisibility = () => {
            if (document.hidden) hiddenAt = Date.now();
            else if (hiddenAt && Date.now() - hiddenAt > AWAY_MS) {
                setLeadSignals({ bookReturned: true });
                hiddenAt = null;
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [key]);

    // Back guard: one extra history entry (same URL, same router state + a marker).
    // Pressing back lands on the real entry; we ask why, then really go back.
    useEffect(() => {
        if (!guardBack) return undefined;
        let listening = false;

        const onPop = () => {
            // Still on (or back to) a guard entry: someone else's dummy entry moved, not ours
            if (window.history.state && window.history.state.lbGuard) return;
            window.removeEventListener('popstate', onPop);
            listening = false;
            requestLeave(() => window.history.back(), { trigger: 'bookLeave', onStay: arm });
        };

        function arm() {
            try {
                const h = window.history;
                if (!(h.state && h.state.lbGuard)) {
                    h.pushState({ ...(h.state || {}), lbGuard: true }, '', window.location.href);
                }
            } catch { return; }
            if (!listening) {
                window.addEventListener('popstate', onPop);
                listening = true;
            }
        }

        arm();
        return () => { window.removeEventListener('popstate', onPop); listening = false; };
    }, [guardBack]);
};

// Exported for pages that want to read whether the bot is mid-survey
export const isLeaveSurveyOpen = () => !!getLeadBotState().leaveRequest;

export default useBookingLeadSignals;
